import React, { useEffect, useState } from "react";
import {
    API_QUERY_BASE_URLS,
    ApiQuery,
    ApiQueryEndpoints,
    FilterMetadata,
    QueryResponse,
} from "~/modules/filters/types";
import { sanitizeFilter } from "./functions/sanitizeFilter";
import { ListPageLayout } from "./ListPageLayout";
import { ExportType, ExportTypes } from "~/modules/downloads/types";
import {
    ApiToolsControlsConfig,
    ExportDataControlsConfig,
    QueryExamplesControlsConfig,
    SearchControlsConfig,
} from "./FilterControls/types";
import { useRouter } from "next/router";
import { isNullOrUndefined } from "~/utils/isNullOrUndefined";
import { useApiQuery } from "./useApiQuery";
import { DataExportProvider } from "~/modules/downloads/components/DataExportProvider";

// // // //
// ENHANCEMENT - modularize the components + hooks in this file

// // // //

// ENHANCEMENT - add error feedback mechanism to UI
function isValidQueryResponse<T>(response: any): response is QueryResponse<T> {
    if (isNullOrUndefined(response)) {
        return false;
    }

    // Check if 'results' is an array
    if (!Array.isArray(response.results)) {
        return false;
    }

    // Check if 'sort' object exists and has the required fields
    if (
        !response.sort ||
        typeof response.sort.field !== "string" ||
        typeof response.sort.direction !== "string"
    ) {
        return false;
    }

    // Check if 'pagination' object exists and all its properties are numbers
    const { pagination } = response;
    if (
        !pagination ||
        typeof pagination.page !== "number" ||
        typeof pagination.perPage !== "number" ||
        typeof pagination.pageCount !== "number" ||
        typeof pagination.resultCount !== "number"
    ) {
        return false;
    }

    return true;
}

// // // //

function generateUrlForView(props: {
    apiQuery: ApiQuery;
    urlParamKey?: string;
    pathname: string;
}): string {
    const { apiQuery, urlParamKey = "query", pathname } = props;

    // Step 1: Convert JSON object to a string
    const jsonStr = JSON.stringify(apiQuery);

    // Step 3: URL encode the compressed data
    const encodedData = encodeURIComponent(jsonStr);

    // Step 4: Build the final URL with the encoded data as a query parameter
    const finalUrl = `${pathname}?${urlParamKey}=${encodedData}`;

    return finalUrl;
}

// // // //

function parseUrlData(props: {
    query: Record<string, any>;
    urlParamKey?: string;
    apiQuery: ApiQuery;
}): ApiQuery {
    const { query, urlParamKey = "query", apiQuery } = props;

    // Step 1: Extract the encoded data from the URL
    const encodedData = query[urlParamKey];

    if (encodedData) {
        try {
            // Step 5: Parse the decompressed data as JSON
            const parsedData = JSON.parse(encodedData);
            // Ensure the parsed data matches the ApiQuery structure
            if (typeof parsedData === "object" && parsedData !== null) {
                return parsedData as ApiQuery;
            } else {
                console.error("Invalid API query data format");
            }
        } catch (error) {
            // Handle any errors that may occur during decompression or parsing
            console.error("Error parsing URL data:", error);
            console.log(error);
        }
    }

    // Return the default apiQuery if no data was found or any step failed
    return apiQuery;
}

/**
 * Read URL search params directly from window.location.
 * Avoids the delay from Next.js router hydration where router.query
 * starts empty for SSG pages and takes several seconds to populate.
 */
function getUrlSearchParams(): Record<string, string> {
    if (typeof window === "undefined") return {};
    const params = new URLSearchParams(window.location.search);
    const query: Record<string, string> = {};
    params.forEach((value, key) => {
        query[key] = value;
    });
    return query;
}

// // // //

// TODO - this should manage "ApiQueryView" state as well
// appliedQueryView: {
//     key: "current",
//     query: appliedQuery.query,
//     content: {
//         label: "",
//     },
//     endpoint: ApiQueryEndpoints.Protocol,
// }

export function ApiQueryStateManager(props: {
    defaultFilters: ApiQuery;
    syncUrl?: boolean;
    modifyApiQuery?: (apiQuery: ApiQuery) => ApiQuery;
    // urlHistory?: boolean;
    children: (childProps: {
        appliedQuery: ApiQueryState;
        internalQuery: ApiQueryState;
        resetQuery: () => void;
    }) => React.ReactNode;
}) {
    const { syncUrl = false } = props;
    const router = useRouter();

    function modifyApiQuery(apiQuery: ApiQuery) {
        return props.modifyApiQuery ? props.modifyApiQuery(apiQuery) : apiQuery;
    }

    // // // // //
    // // // // //
    // ENHANCEMENT - Can change between `useState` and `useRouter` for encapsulating the ApiQuery state
    // ENHANCEMENT - Specify speccific key in URL state for this view (allows for multiple on same page at once)
    // Handled automatically by ApiQueryState.update
    // IDEA - this passes down a URL with the state already included, instead of generating that downstream
    // ^ this is a good idea!

    const initialFilters: ApiQuery = modifyApiQuery(props.defaultFilters);

    const [appliedQuery, setAppliedQuery] =
        React.useState<ApiQuery>(initialFilters);

    const [internalQuery, setInternalQuery] =
        React.useState<ApiQuery>(initialFilters);

    const isInitialMount = React.useRef(true);

    const urlPath = generateUrlForView({
        apiQuery: appliedQuery,
        pathname: router.pathname,
    });

    const [currentUrl, setCurrentUrl] = useState(urlPath);

    // Client-side-only code
    // NOTE - this is a sloppy fix SSG/SSR, re-evaluate approach later
    useEffect(() => {
        setCurrentUrl(`${window.location.origin}${urlPath}`);
    }, []);

    useEffect(() => {
        // console.log("APPLIED QUERY CHANGE");
        const updatedPath = generateUrlForView({
            apiQuery: appliedQuery,
            pathname: router.pathname,
        });
        const updatedUrl = `${window.location.origin}${updatedPath}`;
        setCurrentUrl(updatedUrl);

        // Keeps window history synced
        if (syncUrl) {
            window.history.replaceState("", "", updatedUrl);
        }
        // if (urlHistory) {
        //     router.replace(updatedPath, updatedPath, { shallow: true });
        // }
    }, [appliedQuery]);

    // Respond to router.query changes for programmatic navigation (back/forward, router.push)
    // Initial state is already set from window.location.search, so skip until router is ready
    useEffect(() => {
        if (!router.isReady) return;

        const filtersFromUrl: ApiQuery = parseUrlData({
            query: router.query,
            apiQuery: props.defaultFilters,
        });
        setAppliedQuery(modifyApiQuery(filtersFromUrl));
        setInternalQuery(modifyApiQuery(filtersFromUrl));
    }, [router.query]);

    // On mount: read URL state directly from window.location (fires in ~16ms,
    // much faster than waiting for router.query to hydrate which takes 3-5s on SSG pages)
    // After mount: apply new defaults when props.defaultFilters changes
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            const filtersFromUrl = modifyApiQuery(
                parseUrlData({
                    query: getUrlSearchParams(),
                    apiQuery: props.defaultFilters,
                }),
            );
            setAppliedQuery(filtersFromUrl);
            setInternalQuery(filtersFromUrl);
            return;
        }
        setAppliedQuery(modifyApiQuery(props.defaultFilters));
        setInternalQuery(modifyApiQuery(props.defaultFilters));
    }, [props.defaultFilters]);

    return (
        <>
            {props.children({
                resetQuery: () => {
                    setAppliedQuery(modifyApiQuery(props.defaultFilters));
                    setInternalQuery(modifyApiQuery(props.defaultFilters));

                    // Keeps window history synced
                    if (syncUrl) {
                        window.history.replaceState(
                            "",
                            "",
                            window.location.origin + router.pathname,
                        );
                    }
                    // if (urlHistory) {
                    //     router.push(router.pathname);
                    // }
                },
                appliedQuery: {
                    query: appliedQuery,
                    update: (updatedQuery: ApiQuery) => {
                        setAppliedQuery(modifyApiQuery(updatedQuery));
                    },
                    url: currentUrl,
                },
                internalQuery: {
                    query: internalQuery,
                    update: (updatedQuery: ApiQuery) => {
                        setInternalQuery(modifyApiQuery(updatedQuery));
                    },
                    url: currentUrl,
                },
            })}
        </>
    );
}

// // // //
// // // //

export function ApiQueryUrlStateManager(props: {
    defaultFilters: ApiQuery;
    children: (childProps: {
        appliedQuery: ApiQueryState;
        internalQuery: ApiQueryState;
        resetQuery: () => void;
    }) => React.ReactNode;
}) {
    const router = useRouter();
    // // // // //
    // // // // //
    // ENHANCEMENT - Can change between `useState` and `useRouter` for encapsulating the ApiQuery state
    // ENHANCEMENT - Specify speccific key in URL state for this view (allows for multiple on same page at once)
    // Handled automatically by ApiQueryState.update
    // IDEA - this passes down a URL with the state already included, instead of generating that downstream
    // ^ this is a good idea!
    // console.log(router);

    const appliedQuery: ApiQuery = parseUrlData({
        query: router.query,
        apiQuery: props.defaultFilters,
    });

    const currentUrl = generateUrlForView({
        apiQuery: appliedQuery,
        pathname: router.pathname,
    });

    function setAppliedQuery(updatedQuery: ApiQuery) {
        const url = generateUrlForView({
            apiQuery: updatedQuery,
            pathname: router.pathname,
            // pathname: window.location.origin + router.asPath,
        });
        // console.log("SET APPLIED");
        // console.log(url);
        router.push(url);
    }

    // const [appliedQuery, setAppliedQuery] = React.useState<ApiQuery>(
    //     props.defaultFilters,
    // );

    const [internalQuery, setInternalQuery] =
        React.useState<ApiQuery>(appliedQuery);

    return (
        <>
            {props.children({
                resetQuery: () => {
                    router.push(window.location.origin + router.asPath);
                    // console.log(window.location.origin + router.asPath);
                    setInternalQuery(props.defaultFilters);
                },
                appliedQuery: {
                    query: appliedQuery,
                    update: setAppliedQuery,
                    url: currentUrl,
                },
                internalQuery: {
                    query: internalQuery,
                    update: setInternalQuery,
                    url: currentUrl,
                },
            })}
        </>
    );
}

// // // //

interface State<T> {
    appliedQueryReq: {
        data: QueryResponse<T>;
        loading: boolean;
        error: boolean;
    };
    internalQueryReq: {
        data: QueryResponse<T>;
        loading: boolean;
        error: boolean;
    };
}

/**
 * ApiQueryFetcher
 * Fetches ApiQuery for a specific endpoint via api.rwa.xyz
 */
export function ApiQueryFetcher<T>(props: {
    appliedQuery: ApiQuery;
    internalQuery: ApiQuery;
    endpoint: ApiQueryEndpoints;
    initialState: QueryResponse<T>;
    children: (state: State<T>) => React.ReactNode;
}) {
    const { endpoint, initialState } = props;

    const appliedQuery = {
        ...props.appliedQuery,
        filter: sanitizeFilter(props.appliedQuery.filter),
    };
    const internalQuery = {
        ...props.internalQuery,
        filter: sanitizeFilter(props.internalQuery.filter),
    };

    // Hook for loading + error + protocols state
    const [state, setState] = useState<State<T>>({
        appliedQueryReq: {
            data: initialState,
            loading: false,
            error: false,
        },
        internalQueryReq: {
            data: initialState,
            loading: false,
            error: false,
        },
    });

    // Fetch Applied Query
    const appliedQueryReq = useApiQuery({
        endpoint,
        query: appliedQuery,
    });

    const appliedLoading = appliedQueryReq.isLoading || false;
    const appliedError = appliedQueryReq.error !== undefined;
    const appliedData: QueryResponse<T> = isValidQueryResponse<T>(
        appliedQueryReq.data,
    )
        ? appliedQueryReq.data
        : state.appliedQueryReq.data;

    const internalQueryReq = useApiQuery({
        endpoint,
        query: internalQuery,
    });

    const internalLoading = internalQueryReq.isLoading || false;
    const internalError = internalQueryReq.error !== undefined;
    const internalData: QueryResponse<T> =
        (internalQueryReq.data as QueryResponse<T>) ||
        state.internalQueryReq.data;

    useEffect(() => {
        if (!internalData && !appliedData) {
            return;
        }

        if (internalData && !appliedData) {
            setState({
                ...state,
                internalQueryReq: {
                    ...state.internalQueryReq,
                    data: internalData,
                },
            });
            return;
        }

        if (appliedData && !internalData) {
            setState({
                ...state,
                appliedQueryReq: {
                    ...state.appliedQueryReq,
                    data: appliedData,
                },
            });
            return;
        }

        setState({
            ...state,
            appliedQueryReq: {
                ...state.appliedQueryReq,
                data: appliedData,
            },
            internalQueryReq: {
                ...state.internalQueryReq,
                data: internalData,
            },
        });
    }, [internalData, appliedData]);

    // Expose state via props.children
    return (
        <React.Fragment>
            {props.children({
                appliedQueryReq: {
                    data: state.appliedQueryReq.data,
                    loading: appliedLoading,
                    error: appliedError,
                },
                internalQueryReq: {
                    data: state.internalQueryReq.data,
                    loading: internalLoading,
                    error: internalError,
                },
            })}
        </React.Fragment>
    );
}

// // // //

interface ApiQueryState {
    query: ApiQuery;
    update: (updated: ApiQuery) => void;
    url: string;
}

export function ApiSearchLayout<T>(props: {
    filterMetadata?: FilterMetadata[]; // leave undefined if you don't want to show any filter UI, i.e. just a list with sorting and pagination
    // TODO - update all of these to leverage the new *Config props from FilterControls module
    // enableHeader?: boolean;
    // className?: string;
    emptyStateMessage?: string;
    children: React.ReactNode;
    queryFetcherState: State<T>;
    onResetQuery: () => void;
    appliedQuery: ApiQueryState;
    internalQuery: ApiQueryState;
    title?: string;
    // TODO - add `ControlsProps` interface somewhere so this isn't repeated as much
    controls: {
        // ExportData feature is entirely self-contained, just configured
        exportData?: ExportDataControlsConfig;
        // Configure switching between Basic Search / Pro Search
        search?: Omit<SearchControlsConfig, "onChangeMode">;
        // API Tools Configuration
        apiTools?: ApiToolsControlsConfig;
        // Query Examples Configuration
        queryExamples?: QueryExamplesControlsConfig;
        // Sorting configuration
        sorting?: {
            enabled: boolean;
        };
        // Sharing configuration
        sharing?: {
            enabled: boolean;
            url: string;
        };
    };
}) {
    const {
        filterMetadata = [],
        onResetQuery,
        controls,
        emptyStateMessage = "No Results Found",
        title,
    } = props;
    const appliedQueryState = props.appliedQuery;
    const internalQueryState = props.internalQuery;
    const state = props.queryFetcherState;

    const appliedQuery = appliedQueryState.query;
    const setAppliedQuery = appliedQueryState.update;

    const internalQuery = internalQueryState.query;
    const setInternalQuery = internalQueryState.update;

    // // // //

    const enableExports =
        controls.exportData &&
        controls.exportData !== undefined &&
        controls.exportData.enabled &&
        controls.exportData.exportTableName;

    const enableSorting = controls.sorting?.enabled ?? false;

    return (
        <DataExportProvider>
            {({ isLoading, exportCsv, exportExcel }) => (
                <ListPageLayout
                    title={title}
                    apiDocsBaseUrl={
                        controls.apiTools && controls.apiTools.enabled
                            ? API_QUERY_BASE_URLS[controls.apiTools.endpoint]
                            : undefined
                    }
                    enableProSearch={
                        controls.search ? controls.search.enablePro : false
                    }
                    enableBasicFilters={
                        controls.search ? controls.search.enableBasic : false
                    }
                    appliedQuery={{
                        value: appliedQuery,
                        fetching: state.appliedQueryReq.loading,
                        items: state.appliedQueryReq.data.results,
                        pagination: state.appliedQueryReq.data.pagination,
                        onChange: (updatedQuery: ApiQuery) => {
                            setAppliedQuery(updatedQuery);
                        },
                    }}
                    filterMetadata={{
                        loading: false,
                        items: filterMetadata,
                        queryExamples: controls.queryExamples
                            ? controls.queryExamples.items
                            : [],
                    }}
                    internalQuery={{
                        value: internalQuery,
                        fetching: state.internalQueryReq.loading,
                        resultCount:
                            state.internalQueryReq?.data?.pagination
                                ?.resultCount ?? 0,
                        onChange: (updatedQuery: ApiQuery) => {
                            setInternalQuery(updatedQuery);
                        },
                    }}
                    enableSorting={enableSorting}
                    export={
                        enableExports
                            ? {
                                  onExport: (exportType: ExportType) => {
                                      // Handle CSV Export
                                      if (exportType === ExportTypes.csv) {
                                          exportCsv({
                                              exportTableName: (
                                                  controls.exportData as ExportDataControlsConfig
                                              ).exportTableName,
                                              query: appliedQuery,
                                              exportFilename:
                                                  controls.exportData
                                                      ?.exportFilename,
                                          });
                                      }

                                      // Handle Excel Export
                                      if (exportType === ExportTypes.excel) {
                                          exportExcel({
                                              exportTableName: (
                                                  controls.exportData as ExportDataControlsConfig
                                              ).exportTableName,
                                              query: appliedQuery,
                                              exportFilename:
                                                  controls.exportData
                                                      ?.exportFilename,
                                          });
                                      }

                                      return;
                                  },
                              }
                            : undefined
                    }
                    emptyStateMessage={emptyStateMessage}
                    onResetQuery={() => {
                        onResetQuery();
                    }}
                    sharing={controls.sharing}
                >
                    {props.children}
                </ListPageLayout>
            )}
        </DataExportProvider>
    );
}
